import { AdBreakTypes, IAdRequestConfig } from '../@types/ads'

const MIN_VID_DURATION_FOR_MIDROLL = 8 * 60 // 8 minutes
const MIDROLL_AD_BREAK_WAIT = 5 * 60 // 5 minutes
const MIN_END_BUFFER_FOR_AD = 0 * 60 // 0 minutes
const MIN_AD_SEPARATION_MILISECONDS = 20 * 1000 // 20 seconds
const UPDATE_THROTTLE_MILISECONDS = 1000 // 1 second
const TIME_PROGRESSION_THRESHOLD_MILISECONDS = 100 // 0.1 seconds

// this concept is not enabled for now
const RESET_ADS_ENABLED = false

type IAdsEnabledConfig = {
  [key in AdBreakTypes]?: boolean
}

interface IAdState {
  adBreakType: AdBreakTypes
  playAt: number
  played: boolean
  triggerPlayWindow: IWindow
  resetWindow: IWindow
}

interface IWindow {
  start: number
  end: number
}

const constantlyFalse = () => false

const throttled = (fn: Function) => {
  let isThrottled = false

  return (...args) => {
    if (!isThrottled) {
      isThrottled = true
      setTimeout(() => {
        isThrottled = false
      }, UPDATE_THROTTLE_MILISECONDS)
      return fn(...args)
    }
  }
}

export class AdRules {
  private adMap: Array<IAdState> = []
  private secondsSinceAdPlay = 0
  private previousTime = 0
  public processTimeAndGetCurrentAd: Function

  constructor(duration: number, adsEnabledConfig: IAdsEnabledConfig) {
    this.adMap = this.buildAdMap(duration, adsEnabledConfig)
    this.processTimeAndGetCurrentAd = this.adMap.length
      ? throttled(this.processTimeAndGetCurrentAdBase.bind(this))
      : constantlyFalse
  }

  private buildAdMap(duration: number, adsEnabledConfig: IAdsEnabledConfig) {
    const adMap: Array<IAdState> = []
    if (duration > MIN_VID_DURATION_FOR_MIDROLL && adsEnabledConfig.midroll) {
      let nextStartDelay = MIDROLL_AD_BREAK_WAIT
      while (duration - nextStartDelay > MIN_END_BUFFER_FOR_AD) {
        adMap.push({
          adBreakType: 'midroll',
          playAt: nextStartDelay,
          played: false,
          triggerPlayWindow: { start: 0, end: 15 },
          resetWindow: { start: -60, end: -30 }
        })
        nextStartDelay += MIDROLL_AD_BREAK_WAIT
      }
    }

    return adMap
  }

  private processTimeAndGetCurrentAdBase(
    currentTime: number
  ): IAdRequestConfig | undefined {
    const adMap = this.adMap
    let configToPlay: IAdRequestConfig | undefined

    const timeDifference = currentTime - this.previousTime
    if (
      Math.abs(timeDifference) <=
      UPDATE_THROTTLE_MILISECONDS - TIME_PROGRESSION_THRESHOLD_MILISECONDS
    ) {
      this.secondsSinceAdPlay += UPDATE_THROTTLE_MILISECONDS
      this.previousTime = currentTime
    }

    adMap.forEach((adState) => {
      if (adState.played && this.shouldResetPlayed(adState, currentTime)) {
        adState.played = false
      }

      if (!adState.played && this.shouldTriggerPlay(adState, currentTime)) {
        configToPlay = {
          adBreakType: adState.adBreakType,
          startDelay: adState.playAt
        }
        adState.played = true
        this.secondsSinceAdPlay = 0
      }
    })

    return configToPlay
  }

  private timeInWindow(
    anchorTime: number,
    window: IWindow,
    currentTime: number
  ) {
    const windowStart = anchorTime + window.start
    const windowEnd = anchorTime + window.end
    return currentTime >= windowStart && currentTime <= windowEnd
  }

  private shouldResetPlayed(adState: IAdState, currentTime: number): boolean {
    if (!RESET_ADS_ENABLED) {
      return false
    }
    return this.timeInWindow(adState.playAt, adState.resetWindow, currentTime)
  }

  private shouldTriggerPlay(adState: IAdState, currentTime: number): boolean {
    if (
      this.secondsSinceAdPlay < MIN_AD_SEPARATION_MILISECONDS &&
      adState.adBreakType !== 'nonlinear'
    ) {
      return false
    }

    return this.timeInWindow(
      adState.playAt,
      adState.triggerPlayWindow,
      currentTime
    )
  }
}
